import { fromEntries } from "./genericUtils";
import { NonNullableObject, ExclusiveUserContent } from "../types/common";
import { Assignment, Class, FlashcardSet, Folder, ItemType, Media, Note } from "../graphql/schema";
import { isEqual, omit, difference } from "lodash-es";
import { trimObject } from "./stringUtils";

export const DEFAULT_NOTE_SUMMARY = "";
export const DEFAULT_NOTE_TITLE = "";
export const DEFAULT_NOTE_CONTENT = "";

export const UNTITLED = "Untitled";
export const TITLE_FILLER = "Note";

/**
 * Removes all fields from an object that are null or undefined
 */
export const scrapeEmptyFields = <T extends Record<string, unknown>>(obj: T) => {
    return fromEntries(
        Object.entries(obj).filter(([, val]) => val !== null && val !== undefined)
    ) as NonNullableObject<T>;
};

export const deepScrapeEmptyFields = <T>(obj: T, keysWithAllowedNullValue: (keyof T)[] = []): T => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== "object") return obj;
    if (Array.isArray(obj))
        return obj.map(el => deepScrapeEmptyFields(el, [])).filter(el => el !== null && el !== undefined) as T;
    return Object.fromEntries(
        Object.entries(obj)
            .map(([key, val]) => [key, deepScrapeEmptyFields(val, [])])
            .filter(([key, val]) => {
                if (val === undefined) return false;

                if (keysWithAllowedNullValue.includes(key)) return true;

                if (val === null) return false;

                return true;
            })
    );
};

/**
 * given a base object, create and return a new object without the given keys
 */
export const objectWithout = <T, K extends keyof T>(baseObject: T, ...keys: K[]): Omit<T, K> => {
    const newObject = { ...baseObject };
    for (const key of keys) {
        delete newObject[key];
    }
    return newObject;
};

/**
 * pick certain keys from a given base object
 */
export const pick = <T, K extends keyof T>(baseObject: T, ...keys: K[]): Pick<T, K> => {
    if (!baseObject) return baseObject;

    const newObject = {} as Pick<T, K>;
    for (const key of keys) {
        newObject[key] = baseObject[key];
    }
    return newObject;
};

export const isNote = (
    item: FlashcardSet | Folder | Media | Note | Class | Assignment | ExclusiveUserContent | null | undefined
): item is Note => {
    if (!item) return false;

    return (
        (item as ExclusiveUserContent)?.note?.__typename === "Note" ||
        (item as FlashcardSet | Folder | Media | Note | Assignment)?.__typename === "Note" ||
        (item as any)?.itemType === ItemType.NOTE
    );
};

export const isFlashcardSet = (
    item: FlashcardSet | Folder | Media | Note | Class | Assignment | ExclusiveUserContent | null | undefined
): item is FlashcardSet => {
    if (!item) return false;
    return (
        (item as ExclusiveUserContent)?.flashcardSet?.__typename === "FlashcardSet" ||
        (item as FlashcardSet | Folder | Media | Note | Assignment)?.__typename === "FlashcardSet" ||
        (item as any)?.itemType === ItemType.FLASHCARDSET
    );
};

export const isFolder = (
    item: FlashcardSet | Folder | Media | Note | Class | Assignment | ExclusiveUserContent | null | undefined
): item is Folder => {
    if (!item) return false;

    return (
        (item as ExclusiveUserContent)?.folder?.__typename === "Folder" ||
        (item as FlashcardSet | Folder | Media | Note | Assignment)?.__typename === "Folder" ||
        (item as any)?.itemType === ItemType.FOLDER
    );
};

export const isMedia = (
    item: FlashcardSet | Folder | Media | Note | Class | Assignment | ExclusiveUserContent | null | undefined
): item is Media => {
    if (!item) return false;

    return (
        (item as ExclusiveUserContent)?.media?.__typename === "Media" ||
        (item as FlashcardSet | Folder | Media | Note | Assignment)?.__typename === "Media" ||
        (item as any)?.itemType === ItemType.MEDIA
    );
};

export const isAssignment = (
    item: FlashcardSet | Folder | Media | Note | Class | Assignment | ExclusiveUserContent | null | undefined
): item is Assignment => {
    if (!item) return false;

    return (
        (item as ExclusiveUserContent)?.assignment?.__typename === "Assignment" ||
        (item as FlashcardSet | Folder | Media | Note | Class | Assignment)?.__typename === "Assignment" ||
        (item as any)?.itemType === ItemType.ASSIGNMENT
    );
};

export const isClass = (
    item: FlashcardSet | Folder | Media | Note | Class | Assignment | ExclusiveUserContent | null | undefined
): item is Class => {
    if (!item) return false;

    return (
        (item as ExclusiveUserContent)?.course?.__typename === "Class" ||
        (item as FlashcardSet | Folder | Media | Note | Class | Assignment)?.__typename === "Class" ||
        (item as any)?.itemType === ItemType.CLASS
    );
};

export const getItemIdFromUserContentItem = (item: Note | FlashcardSet | Folder | Media | Assignment | Class) => {
    if (isNote(item)) return item.noteId;
    if (isFlashcardSet(item)) return item.flashcardSetId;
    if (isFolder(item)) return item.folderId;
    if (isMedia(item)) return item.mediaId;
    if (isAssignment(item)) return item.assignmentId;
    if (isClass(item)) return item.classId;
    return null;
};

export const getItemTypeFromUserContentItem = (
    item: Note | FlashcardSet | Folder | Media | Assignment | Class | null
) => {
    if (isNote(item)) return ItemType.NOTE;
    if (isFlashcardSet(item)) return ItemType.FLASHCARDSET;
    if (isFolder(item)) return ItemType.FOLDER;
    if (isMedia(item)) return ItemType.MEDIA;
    if (isAssignment(item)) return ItemType.ASSIGNMENT;
    if (isClass(item)) return ItemType.CLASS;
    return null;
};

export const getItemLabelFromItemType = (itemType: ItemType) => {
    if (itemType === ItemType.NOTE) return "Note";
    if (itemType === ItemType.FLASHCARDSET) return "Flashcard Set";
    if (itemType === ItemType.FOLDER) return "Folder";
    if (itemType === ItemType.MEDIA) return "Media";
    if (itemType === ItemType.ASSIGNMENT) return "Assignment";
    if (itemType === ItemType.CLASS) return "Class";
    return "";
};

/**
 * only pick the updated fields from the new object
 */

export const pickUpdatedFields = <T extends Record<string, unknown> | Record<string, unknown>[]>({
    newItem,
    oldItem,
    keys,
}: {
    oldItem: T;
    newItem: T;
    keys: string[];
}): T => {
    if (Array.isArray(newItem)) {
        return newItem.map((newItemVal, index) => {
            const oldItemVal = oldItem[index];
            return pickUpdatedFields({ newItem: newItemVal, oldItem: oldItemVal, keys });
        }) as T;
    }

    return Object.keys(newItem).reduce((acc, key) => {
        if (isEqual(newItem[key], oldItem[key]) && !keys.includes(key)) return acc;
        acc[key] = newItem[key];
        return acc;
    }, {} as T);
};

export const isValidUrl = (val: string | null | undefined) => /^(\S+):(\/\/)?\S+$/.test(val ?? "");

const specialCharRegex =
    /[\u{1D400}-\u{1D433}\u{1D434}-\u{1D467}\u{1D468}-\u{1D49B}\u{1D49C}-\u{1D4CF}\u{1D670}-\u{1D6A3}\u{1D5A0}-\u{1D5D3}\u{1D5A0}-\u{1D5D3}\u{1D7CE}-\u{1D7F5}\u{1D7D8}-\u{1D7E1}\u{1D7E2}-\u{1D7EB}\u{1D7F6}-\u{1D7FF}\u{FF10}-\u{FF19}\u{FF01}-\u{FF5E}\u{3000}]/u;

export const normalizeText = (input: string): string => {
    if (!input) return input;

    // Early exit if no special characters are found
    if (!specialCharRegex.test(input)) {
        return input;
    }

    return Array.from(input)
        .map(char => {
            const code = char.codePointAt(0);

            if (!code) return char;

            // Handle bold characters (uppercase and lowercase)
            if (code >= 0x1d400 && code <= 0x1d419) {
                // Bold uppercase A-Z (𝐀 - 𝐙)
                return String.fromCodePoint(code - 0x1d400 + 0x41); // Uppercase A-Z
            }
            if (code >= 0x1d41a && code <= 0x1d433) {
                // Bold lowercase a-z (𝐚 - 𝐳)
                return String.fromCodePoint(code - 0x1d41a + 0x61); // Lowercase a-z
            }

            // Handle italic characters (uppercase and lowercase)
            if (code >= 0x1d434 && code <= 0x1d44d) {
                // Italic uppercase A-Z (𝑨 - 𝑭)
                return String.fromCodePoint(code - 0x1d434 + 0x41); // Uppercase A-Z
            }
            if (code >= 0x1d44e && code <= 0x1d467) {
                // Italic lowercase a-z (𝑎 - 𝑧)
                return String.fromCodePoint(code - 0x1d44e + 0x61); // Lowercase a-z
            }

            // Handle monospace characters (uppercase and lowercase)
            if (code >= 0x1d670 && code <= 0x1d689) {
                // Monospace uppercase A-Z (𝙰 - 𝚉)
                return String.fromCodePoint(code - 0x1d670 + 0x41); // Uppercase A-Z
            }
            if (code >= 0x1d68a && code <= 0x1d6a3) {
                // Monospace lowercase a-z (𝚊 - 𝚣)
                return String.fromCodePoint(code - 0x1d68a + 0x61); // Lowercase a-z
            }

            // Handle bold italic characters (uppercase and lowercase)
            if (code >= 0x1d468 && code <= 0x1d481) {
                // Bold italic uppercase A-Z (𝑨 - 𝑭)
                return String.fromCodePoint(code - 0x1d468 + 0x41); // Uppercase A-Z
            }
            if (code >= 0x1d482 && code <= 0x1d49b) {
                // Bold italic lowercase a-z (𝒶 - 𝓏)
                return String.fromCodePoint(code - 0x1d482 + 0x61); // Lowercase a-z
            }

            // Handle script characters (uppercase and lowercase)
            if (code >= 0x1d49c && code <= 0x1d4b5) {
                // Script uppercase A-Z (𝒜 - 𝒵)
                return String.fromCodePoint(code - 0x1d49c + 0x41); // Uppercase A-Z
            }
            if (code >= 0x1d4b6 && code <= 0x1d4cf) {
                // Script lowercase a-z (𝒶 - 𝓏)
                return String.fromCodePoint(code - 0x1d4b6 + 0x61); // Lowercase a-z
            }

            // Handle sans-serif bold characters (uppercase and lowercase)
            if (code >= 0x1d5a0 && code <= 0x1d5b9) {
                // Sans-serif bold uppercase A-Z
                return String.fromCodePoint(code - 0x1d5a0 + 0x41); // Uppercase A-Z
            }
            if (code >= 0x1d5ba && code <= 0x1d5d3) {
                // Sans-serif bold lowercase a-z
                return String.fromCodePoint(code - 0x1d5ba + 0x61); // Lowercase a-z
            }

            // Handle bold numbers (𝟬-𝟵)
            if (code >= 0x1d7ce && code <= 0x1d7d7) {
                // Bold digits 𝟬-𝟵
                return String.fromCodePoint(code - 0x1d7ce + 0x30); // Digits 0-9
            }

            if (code >= 0x1d7ec && code <= 0x1d7f5) {
                // Bold digits 𝟬-𝟵
                return String.fromCodePoint(code - 0x1d7ec + 0x30); // Digits 0-9
            }

            // Handle double-struck numbers (𝟘-𝟡)
            if (code >= 0x1d7d8 && code <= 0x1d7e1) {
                // Double-struck digits 𝟘-𝟡
                return String.fromCodePoint(code - 0x1d7d8 + 0x30); // Digits 0-9
            }

            // Handle sans-serif numbers (𝟢-𝟫)
            if (code >= 0x1d7e2 && code <= 0x1d7eb) {
                // Sans-serif digits 𝟢-𝟫
                return String.fromCodePoint(code - 0x1d7e2 + 0x30); // Digits 0-9
            }

            // Handle monospace numbers (𝟶-𝟿)
            if (code >= 0x1d7f6 && code <= 0x1d7ff) {
                // Monospace digits 𝟶-𝟿
                return String.fromCodePoint(code - 0x1d7f6 + 0x30); // Digits 0-9
            }

            // Handle full-width numbers (０-９)
            if (code >= 0xff10 && code <= 0xff19) {
                return String.fromCodePoint(code - 0xff10 + 0x30); // 0-9
            }

            if (code === 0x3000) {
                return String.fromCodePoint(0x20); // Convert to regular space
            }

            // General handling for full-width ASCII equivalents
            if (code >= 0xff01 && code <= 0xff5e) {
                return String.fromCodePoint(code - 0xfee0); // Convert full-width to regular ASCII
            }

            // Return the character unchanged if it's not part of a specific mapped range
            return char;
        })
        .join("");
};

// From terraform_utils/utils/dataCleaning.ts

export const findKeyInValue = (object, value) => {
    return Object.keys(object).find(key => !!object[key]?.[value]);
};

export const keepKeys = (obj: { [key: string]: any }, listOfFields: string[]) => {
    const keysToRemove: string[] = difference(Object.keys(obj), listOfFields);
    return omit(obj, keysToRemove);
};

export const removeKeys = (obj: any, listOfKeys: string[]) => {
    return omit(obj, listOfKeys);
};

export const flipSimpleObjectKeys = (item: { [key: string]: any }) => {
    return Object.keys(item).reduce((obj, key) => {
        return {
            // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
            ...obj,
            [item[key]]: key,
        };
    }, {});
};

export const logAndThrow = ({
    error = "",
    extra = "",
    authorizationError = false,
    input,
    name,
    shouldThrow = true,
    shouldLog = true,
    returnItem = null,
}) => {
    if (shouldLog) {
        if (authorizationError) {
            // biome-ignore lint/suspicious/noConsole: <explanation>
            console.warn("[SOFT_ERROR] - [AUTHORIZATION]", name, trimObject(input));
        } else {
            // biome-ignore lint/suspicious/noConsole: <explanation>
            console.error("[HARD_ERROR]", name, error, extra, trimObject(input));
        }
    }

    if (shouldThrow) {
        throw new Error(`${name} - ${error}`);
    } else {
        return returnItem;
    }
};

export const tryParsingArray = (x: string) => {
    let item = x.slice(x.indexOf("["), x.lastIndexOf("]") + 1);
    let attempts = 0;
    const maxAttempts = 10;

    try {
        return JSON.parse(item);
    } catch {
        // Initial correction attempt
        item = item.slice(0, item.lastIndexOf("}") + 1) + "]";
    }

    while (item.includes("}") && attempts < maxAttempts) {
        attempts++;
        try {
            return JSON.parse(item);
        } catch {
            item = item.slice(0, item.length - 2);
            item = item.slice(0, item.lastIndexOf("}") + 1) + "]";
        }
    }

    // biome-ignore lint/suspicious/noConsole: <explanation>
    console.log("Error parsing AI output after multiple attempts", x);
    return [];
};

const filteredPhrases = [
    "*VER—",
    "VOIR —",
    "Pelicula Completa",
    "Película completa",
    "película Completa",
    "Pelicula Completa",
    "PELICULA COMPLETA",
    "La Pelicula",
    "FULLMOVIE",
    "PELISPLUS",
    "PELISFLIX",
    "Leaked Video",
    "Leaked Sex",
    "Sex Video",
    "sex Video",
    "Leak Video",
    "Full Sex",
    "Sex Videos",
    "sex Video",
    "virals!clips",
    "STREAMING MEDIA",
    "Full Video Link",
    "Watch Now Free",
    "Stream Free",
    "HD Free Stream",
    "Free Movie Online",
    "Watch Full",
    "Hot Scene",
    "Adult Movie",
    "🔞",
    "Uncensored Video",
    "Full Leaked",
    "Explicit Video",
    "Full Movie",
    "Click Here to Watch",
    "No Ads",
    "Movie HD Online",
    "Live Cam Show",
    "Hot Girls",
    "Private Video",
    "Sex Tape",
    "OnlyFans Leak",
    "Premium Content Free",
    "DM for Link",
    "Message for Full",
    "Get Full Access",
    "Watch Free Online",
    "Movie Download",
    "Stream in HD",
    "Leaked Footage",
    "Exclusive Clip",
    "Download Full",
    "No Subscription Required",
    "Watch Full Episode",
    "Complete Film",
    "Hot Video",
    "Viral Clip",
    "Full Viral Video",
    "Viral Video Leak",
    "Porn video",
    "Porn HD",
    "Download Free",
    "Full HD",
    "full HD",
    "FULL/HD",
    "Full.HD",
    "FULL 4K",
    "720p,480p",
    "4K ULTRA-HD",
    "📺📱👉",
    "Unlocked Video",
    "Watch Now",
    "Premium Leak",
    "Streaming Service Free",
    "New Leak",
    "Free Trial Watch",
    "Top Leak",
    "Viral Leak",
    "Full Viral Video",
    "Viral Video Leak",
    "pornhub Video",
    "PornHub Video",
    "Nude Leak",
    "Scandal Video",
    "New Episode Free",
    "Access Free Content",
    "100% Free Streaming",
    "Explicit Scene",
    "Full Movie Download",
    "Sexual Content",
    "Unreleased Clip",
    "Get Full Movie",
    "Hot Leak",
    "Trending Video",
    "Unlocked Film",
    "Live Show Now",
    "See Full Video",
    "XXX Free",
    "Stream Uncensored",
    "Stream Full Episode",
    "Nude Video",
    "Hot Sexy",
    "Ultra HD 4K",
    "Alta deFinizione",
    "Altadefinizione",
    "Film Completo",
    "Filme Completo",
    "Filme completo",
    "filme Completo",
    "FILME COMPLETO",
    "Film Complete",
    "Streaming ITA",
    "GUARDA FILM",
    "FilmeFlix",
    "온라인 버전",
    "ดูหนังออนไลน์",
    "➤➤➤",
    "Cash App 2025",
    "Cash App Money",
    "Free Gift Card",
    "Money Generator",
    "FREE GENERATOR",
    "Gift Card Generator",
    "gift card code generator",
    "Gift Card Code Generator",
    "Click Here To Get",
    "CLICK HERE TO GET",
    "CLICK HERE TO FREE",
    "GET NOW FREE",
    "GET FREE NOW",
    "Click Here To Claim",
    "Gift Card Codes",
    "Gift Card Code",
    "Gift Card Codes Generator",
    "Codes Generator",
    "Gift Card Generator",
    "Unlimited Credits Generator",
    "Hack Unlimited Credits",
    "free coins hack",
    "uflix24.com",
    "spyhome.shop",
    "joswatch.online",
    "OnlyFans leak",
    "OnlyFans",
    "onlyfans",
    "coupon bundle",
    "COUPON FREE",
].map(phrase => normalizeText(phrase));

export const checkMaliciousInput = (content: string) => {
    if (!content) return false;

    // List of phrases to filter

    // Check if any filtered phrase is included in the content
    return filteredPhrases.some(phrase => normalizeText(content)?.includes(phrase));
};

// List of reputable root domains
const reputableDomains = [
    "wikipedia.org",
    "khanacademy.org",
    "coursera.org",
    "edx.org",
    "udemy.com",
    "futurelearn.com",
    "openstax.org",
    "mit.edu",
    "stanford.edu",
    "harvard.edu",
    "berkeley.edu",
    "yale.edu",
    "cam.ac.uk",
    "ox.ac.uk",
    "jstor.org",
    "springer.com",
    "nature.com",
    "sciencedirect.com",
    "pubmed.ncbi.nlm.nih.gov",
    "researchgate.net",
    "britannica.com",
    "sciencemag.org",
    "biorxiv.org",
    "arxiv.org",
    "plos.org",
    "projecteuclid.org",
    "sagepub.com",
    "taylorandfrancis.com",
    "hindawi.com",
    "oup.com",
    "cornell.edu",
    "princeton.edu",
    "columbia.edu",
    "upenn.edu",
    "uchicago.edu",
    "dartmouth.edu",
    "brown.edu",
    "ucsd.edu",
    "ucla.edu",
    "utexas.edu",
    "gatech.edu",
    "umich.edu",
    "northwestern.edu",
    "duke.edu",
    "vanderbilt.edu",
    "nyu.edu",
    "usc.edu",
    "caltech.edu",
    "uclahealth.org",
    "who.int",
    "unesco.org",
    "un.org",
    "nasa.gov",
    "noaa.gov",
    "nsf.gov",
    "nih.gov",
    "nist.gov",
    "hhs.gov",
    "cdc.gov",
    "data.gov",
    "loc.gov",
    "ed.gov",
    "doe.gov",
    "imf.org",
    "worldbank.org",
    "oecd.org",
    "cambridge.org",
    "scholastic.com",
    "nobelprize.org",
    "ted.com",
    "edutopia.org",
    "eschoolnews.com",
    "commonlit.org",
    "act.org",
    "collegeboard.org",
    "ets.org",
    "kaptest.com",
    "quizlet.com",
    "ck12.org",
    "mathtv.com",
    "desmos.com",
    "geogebra.org",
    "wolframalpha.com",
    "symbolab.com",
    "code.org",
    "scratch.mit.edu",
    "codecademy.com",
    "freecodecamp.org",
    "udacity.com",
    "brilliant.org",
    "carnegiemellon.edu",
    "rice.edu",
    "wisc.edu",
    "illinois.edu",
    "psu.edu",
    "umd.edu",
    "virginia.edu",
    "osu.edu",
    "asu.edu",
    "indiana.edu",
    "msu.edu",
    "ncsu.edu",
    "fsu.edu",
    "ufl.edu",
    "purdue.edu",
    "rpi.edu",
    "anu.edu.au",
    "utoronto.ca",
    "ubc.ca",
    "geeksforgeeks.org",
    "history.com",
    "colorado.edu",
    "macmillanlearning.com",
    "dictionary.com",
    "w3schools.com",
    "tophat.com",
    "pbs.org",
    "wiktionary.org",
    "mheducation.com",
    "gsu.edu",
    "python.org",
    "wikimedia.org",
    "knowt.com",
];

export const checkForNonReputableLinks = (content: string): boolean | string => {
    if (!content) return false;

    // Regular expression to extract href values from <a> tags
    const anchorPattern = /<a[^>]+href="([^"]+)"/gi;

    // Function to extract the root domain
    const extractRootDomain = (domain: string): string | null => {
        const parts = domain.split(".");
        if (parts.length < 2) return null; // Invalid domain
        const tld = parts.pop(); // Top-Level Domain (e.g., "com")
        const secondLevel = parts.pop(); // Second-Level Domain (e.g., "google")
        return secondLevel && tld ? `${secondLevel}.${tld}` : null;
    };

    let match: RegExpExecArray | null = anchorPattern.exec(content); // Initial match
    while (match !== null) {
        const href = match[1]; // Extract the URL

        // Extract the domain from the URL
        try {
            const url = new URL(href);
            const fullDomain = url.hostname;
            const rootDomain = extractRootDomain(fullDomain);

            // Check if either the full domain or the root domain is in the reputable list
            if (!reputableDomains.includes(fullDomain) && rootDomain && !reputableDomains.includes(rootDomain)) {
                return fullDomain; // Found a non-reputable link
            }
        } catch {
            // invalid URLs
            return true;
        }

        match = anchorPattern.exec(content); // Get the next match
    }

    return false; // No non-reputable links found
};

export const checkForPhoneNumbers = (content: string): boolean => {
    if (!content) return false;

    // Regex to extract potential phone numbers
    const phonePatterns = [
        /\+1-\d{3}-\d{3}-\d{4}/g, // e.g., +1-801-614-4796
        /1-\d{3}-\d{3}-\d{4}/g, // e.g., 1-801-614-4796
        /\(\d{3}\)-\d{3}-\d{4}/g, // e.g., (888)-202-1579
        /\(\d{3}\)\s\d{3}-\d{4}/g, // e.g., (866)-650-5315
        /\+1\s\(\d{3}\)\s\d{3}-\d{4}/g, // e.g., +1 (866) 650-5315
        /\+52\(\d{3}\)\s\d{6}/g, // e.g., +52(800) 9530821
        /\+52\(\d{3}\)\d{6}/g, // e.g., +52(800)9530821
        /\+52\s\(\d{3}\)\s\d{6}/g, // e.g., +52 (800) 9530821
        /\+52\(\d{3}\)\s\d{6}/g, // e.g., +52(800) 9530821
        /\+1-\d{3}-\d{4}-\d{4}/g, // e.g., +1-856-804-2257
        /\+1\s\(\d{3}\)-\d{3}-\d{4}/g, // e.g., +1 (866) 650-5315
        /\d{1}-\d{3}-\d{3}-\d{4}/g, // e.g., 1-838-300-3830
        /\+52\s\(\d{3}\)\s\d{6}/g, // e.g., +52 (800) 9530821
        /\+1\s\d{3}-\d{3}-\d{4}/g, // e.g., +1-838-300-3830
        /\+1\s\d{3}\s\d{3}\s\d{4}/g, // e.g., +1 888 891 7221
        /\+1\s\d{3}-\d{3}-\d{4}/g, // e.g., +1 888-891-7221
        /\+1\s\(\d{3}\)\s\d{3}\s\d{4}/g, // e.g., +1 (888) 891 7221
        /\+44\s\(\d{3}\)-\d{3}-\d{4}/g, // e.g., +44 (204)-586-5394
        /\+44\s\d{3}\s\d{3}\s\d{4}/g, // e.g., +44 204 586 5394
        /\d{3}\*{1}\d{3}\*{1}\d{4}/g, // e.g., 800*555*1212
        /(?:\+?\d{1,2}[-\s()]*)?(?:\(?\d{3}\)?[-\s]?)\d{3}[-\s]?(?![\da-f])[^\da-f]?[-\s]?\d{4}(?![\w\-])/g, // e.g., (800) 555-1212, 800-555-1212
        /^1\s*-\s*\d{3}\s*-\s*\d{3}\s*-\s*\d{4}$/g, // e.g., 1 - 800 - 555 - 1212
        /^\d{3}\s*-\s*\d{3}\s*-\s*\d{4}$/g, // e.g., 800 - 555 - 1212
        /^\(1\)\-\(\(\d{3}\)\)\-\(\d{3}\)\-\(\d{4}\)$/g, // e.g., (1)-(800)-(555)-(1212)
        /^\+\d{2}\-\d{2}\-\d{4}\-\d{4}$/g, // e.g., +91-80-1234-5678
        /^\d{3}\.\d{3}\.\d{4}$/g, // e.g., 800.555.1212
        /^\d{3}--\d{3}--\d{4}$/g, // e.g., 800--555--1212
        /^\d{3}\-\(\d{3}\)\-\d{4}$/g, // e.g., 800-(555)-1212
        /\d{3}::\d{3}-\d{4}/g, // e.g., 800::555-1212
        /\(\d{3}\)-+ \d{3}-+ \d{4}/g, // e.g., (800)- 555- 1212
        /\d{3}-\d{3}-\d{4}/g, // e.g., 800-555-1212
        /\d{3}!!\d{3}!!\d{4}/g, // e.g., 800!!555!!1212
        /\d{3}-\d{3}\(\d{4}\)/g, // e.g., 800-555(1212)
        /\d{3}\W+\d{3}\W+\d{4}/g, // e.g., 800-555-1212
        /\(?\d{3}\)?-\(?\d{3}\)?-\(?\d{2}\)\(?\d{2}\)?/g, // e.g., (800)-(555)-(12)(12)
        /\+\[\d{10}\]/g, // e.g., +[8005551212]
        /\+\d{12}/g, // e.g., +8005551212
    ];

    // Combine all matches from all patterns
    return phonePatterns.some(pattern => content.match(pattern));
};

export const checkForEmailAddresses = (content: string): boolean => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+\.*$/;

    return emailPattern.test(content);
};

export const checkForPhoneNumbersOLD = (content: string): boolean => {
    if (!content) return false;

    // Regex to match phone numbers allowing random separators
    const phonePattern = /(\+?\d{1,2}[-\s→]*)?(\(?\d{3}\)?[-\s→]*)\d{3}[-\s→]*\d{4}/g;

    return phonePattern.test(content);
};
